import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaTimes, FaPaperPlane, FaTrashAlt } from 'react-icons/fa';
import { quickpointsAPI } from '../utils/api';
import { toast } from 'sonner';

const EMOJI_OPTIONS = ['😊', '🔥', '💯', '✨', '💡', '🎯', '🚀', '💪', '❤️', '🤔', '😎', '🎉', '👀', '⚡', '🌟', '💭'];

const QuickPointBar = () => {
  const [quickpoints, setQuickpoints] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [text, setText] = useState('');
  const [emoji, setEmoji] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  const loadQuickpoints = useCallback(async () => {
    try {
      const res = await quickpointsAPI.getAll();
      setQuickpoints(res.data);
    } catch (e) { console.error("QuickPoints load failed:", e); }
  }, []);

  useEffect(() => { loadQuickpoints(); }, [loadQuickpoints]);

  const handleCreate = async () => {
    if (!text.trim() && !emoji) return;
    setLoading(true);
    try {
      await quickpointsAPI.create({ text: text.trim(), emoji });
      setText('');
      setEmoji('');
      setShowCreate(false);
      loadQuickpoints();
      toast.success('QuickPoint shared!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create QuickPoint');
    } finally { setLoading(false); }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    const own = quickpoints.find(q => q.user_id === currentUser.id);
    if (!own) return;
    try {
      await quickpointsAPI.delete(own.id);
      setShowDeleteConfirm(false);
      loadQuickpoints();
      toast.success('QuickPoint removed');
    } catch { toast.error('Failed to delete'); }
  };

  const handleTap = (qp) => {
    if (qp.user_id === currentUser.id) return;
    navigate(`/messages?startChat=${qp.user_id}`);
  };

  const hasOwnQP = quickpoints.some(q => q.user_id === currentUser.id);
  const ownQP = quickpoints.find(q => q.user_id === currentUser.id);

  return (
    <div className="mb-4" data-testid="quickpoint-bar">
      <div className="flex items-center space-x-4 overflow-x-auto pb-2 scrollbar-hide">
        {/* Create / Own QuickPoint */}
        {!hasOwnQP ? (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="quickpoint-create-btn"
            className="flex-shrink-0 flex flex-col items-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A90E2]/10 to-[#A855F7]/10 border-2 border-dashed border-[#4A90E2]/40 flex items-center justify-center hover:border-[#4A90E2] transition-colors">
              <FaPlus className="text-[#4A90E2]" size={16} />
            </div>
            <span className="text-[10px] text-gray-400 font-medium mt-1">Your note</span>
          </button>
        ) : (
          <div
            data-testid="quickpoint-own"
            className="flex-shrink-0 flex flex-col items-center relative"
          >
            <div className="relative">
              <img
                src={currentUser.avatar_url || `https://ui-avatars.com/api/?name=${currentUser.full_name || 'U'}&size=64`}
                alt="You"
                className="w-16 h-16 rounded-full object-cover ring-2 ring-[#4A90E2]"
              />
              <button
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="quickpoint-delete-btn"
                className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center transition-colors shadow-sm"
              >
                <FaTimes className="text-white" size={9} />
              </button>
            </div>
            <div className="mt-1.5 bg-white rounded-lg px-2.5 py-1 shadow-sm border border-gray-100 max-w-[140px]">
              <p className="text-[11px] text-gray-800 font-medium text-center leading-tight" style={{ wordBreak: 'break-word' }}>
                {ownQP?.emoji} {ownQP?.text}
              </p>
            </div>
            <span className="text-[10px] text-[#4A90E2] font-semibold mt-0.5">You</span>
          </div>
        )}

        {/* Others' QuickPoints */}
        {quickpoints.filter(q => q.user_id !== currentUser.id).map(qp => (
          <button
            key={qp.id}
            onClick={() => handleTap(qp)}
            data-testid={`quickpoint-${qp.id}`}
            className="flex-shrink-0 flex flex-col items-center group"
          >
            <div className="relative">
              <img
                src={qp.avatar_url || `https://ui-avatars.com/api/?name=${qp.full_name || qp.username}&size=64`}
                alt={qp.username}
                className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-[#4A90E2] transition-colors"
              />
            </div>
            <div className="mt-1.5 bg-white rounded-lg px-2.5 py-1 shadow-sm border border-gray-100 max-w-[140px]">
              <p className="text-[11px] text-gray-800 font-medium text-center leading-tight" style={{ wordBreak: 'break-word' }}>
                {qp.emoji} {qp.text}
              </p>
            </div>
            <span className="text-[10px] text-gray-500 font-medium truncate max-w-[80px] mt-0.5">{qp.username}</span>
          </button>
        ))}

        {quickpoints.length === 0 && !hasOwnQP && (
          <p className="text-xs text-gray-300 ml-2 whitespace-nowrap">Share a quick thought with your followers</p>
        )}
      </div>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 10 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-xl text-center"
              data-testid="quickpoint-delete-modal"
            >
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                <FaTrashAlt className="text-red-500" size={18} />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">Remove QuickPoint?</h3>
              <p className="text-sm text-gray-500 mb-4">This will remove your note for everyone</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  data-testid="quickpoint-delete-cancel"
                  className="flex-1 py-2.5 rounded-full border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  data-testid="quickpoint-delete-confirm"
                  className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-medium text-sm hover:bg-red-600 transition-colors"
                >
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create QuickPoint Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowCreate(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl"
              data-testid="quickpoint-create-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 font-arthaus">QuickPoint</h3>
                <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
              </div>

              <div className="relative mb-3">
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value.slice(0, 60))}
                  placeholder="What's on your mind?"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-gray-900 placeholder:text-gray-400 text-sm resize-none focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] outline-none"
                  rows={2}
                  data-testid="quickpoint-text-input"
                  autoFocus
                />
                <span className={`absolute bottom-3 right-3 text-[10px] ${text.length >= 55 ? 'text-red-400' : 'text-gray-300'}`}>
                  {text.length}/60
                </span>
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Add an emoji</p>
                <div className="flex flex-wrap gap-1.5">
                  {EMOJI_OPTIONS.map(e => (
                    <button
                      key={e}
                      onClick={() => setEmoji(emoji === e ? '' : e)}
                      data-testid={`quickpoint-emoji-${e}`}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                        emoji === e ? 'bg-[#4A90E2]/10 ring-2 ring-[#4A90E2] scale-110' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {(text || emoji) && (
                <div className="bg-gray-50 rounded-xl p-3 mb-4 text-center">
                  <p className="text-sm text-gray-400 mb-1">Preview</p>
                  <div className="bg-white rounded-full inline-block px-4 py-1.5 shadow-sm border border-gray-100">
                    <span className="text-sm font-medium text-gray-800">{emoji} {text}</span>
                  </div>
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={loading || (!text.trim() && !emoji)}
                data-testid="quickpoint-submit-btn"
                className="w-full bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white rounded-full py-3 font-bold text-sm transition-all hover:opacity-90 hover:scale-105 active:scale-95 disabled:opacity-40 flex items-center justify-center space-x-2"
              >
                <FaPaperPlane size={12} />
                <span>{loading ? 'Sharing...' : 'Share QuickPoint'}</span>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickPointBar;
